import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App, URLOpenListenerEvent } from "@capacitor/app";

/**
 * Bridges native navigation events into the in-app (SPA) router. Must be mounted
 * inside the Router (it uses useHistory):
 *  - `appUrlOpen` deep links (universal links + custom scheme), preserving the query
 *    string (e.g. ?invitation=TOKEN).
 *  - a `push-navigate` window event dispatched by the FCM tap handler
 *    (helper/notifications.ts) so a tapped push notification routes correctly.
 */
const AppUrlListener: React.FC = () => {
  const history = useHistory();

  // Push-notification taps (dispatched as a window event by the FCM tap handler).
  useEffect(() => {
    const onPushNavigate = (e: Event) => {
      const target = (e as CustomEvent).detail?.target;
      if (typeof target === "string" && target) history.push(target);
    };
    window.addEventListener("push-navigate", onPushNavigate);
    return () => window.removeEventListener("push-navigate", onPushNavigate);
  }, [history]);

  // Native deep links.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const handle = App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      try {
        const url = new URL(event.url);
        // Universal links (https://host/path) put the path in url.pathname;
        // custom-scheme links (app://path) put the first segment in url.host.
        const isHttp = url.protocol === "http:" || url.protocol === "https:";
        let path = isHttp ? url.pathname : `/${url.host}${url.pathname}`;
        if (!path.startsWith("/")) path = `/${path}`;
        history.push(path + url.search + url.hash);
      } catch {
        /* ignore malformed deep links */
      }
    });
    return () => {
      handle.then((h) => h.remove()).catch(() => {});
    };
  }, [history]);

  return null;
};

export default AppUrlListener;
